import axios from 'axios'
import { sameOriginUsersApiUrl } from '../../app/axios/const'

// Mirror of CORE's IdentityConfigResponse (Models/Datasets/Auth) — the per-Tenant MSAL bootstrap the
// SPA fetches at boot from GET /api/v1/auth/identity-config (E7 F7-3).
// One federation provider the SPA can route a sign-in to. Mirrors CORE's IdentityConfigFederationProvider:
// domainHint is a SINGLE value (the OIDC domain_hint MSAL passes at /authorize — External ID has no native
// email-domain HRD, so routing is app-driven), and hasUnverifiedDomain flags a provider with a
// grandfathered domain not yet linked to a verified domain (drives the login caution icon, E12 F12-6).
export interface IdentityConfigFederationProvider {
  id: number
  displayName: string
  providerType: string
  providerTypeId: number
  domainHint: string
  authority: string
  hasUnverifiedDomain: boolean
}

export interface IdentityConfig {
  displayName: string
  authority: string
  clientId: string
  apiScope: string
  localAccountsAllowed: boolean
  federationProviders: IdentityConfigFederationProvider[]
}

// v2: the federationProviders element shape changed (single domainHint + providerType/hasUnverifiedDomain),
// and this is the first SPA build to READ those fields. Bumping the key forces a fresh fetch instead of
// trusting a body a prior SPA session cached, so no code path reads a provider that predates the fields.
const STORAGE_KEY = 'lsk.identityConfig.v2'
const ETAG_KEY = 'lsk.identityConfig.etag.v2'

// Synchronous read of the identity config the loader has already cached this session (populated by
// fetchIdentityConfig during the MSAL bootstrap). Lets pre-auth UI — the LoginPage (E8 F8-1) — show the
// workspace display name without re-fetching. Returns null if the cache is absent or unparseable.
export function getCachedIdentityConfig(): IdentityConfig | null {
  const raw = sessionStorage.getItem(STORAGE_KEY)
  if (!raw) {
    return null
  }
  try {
    return JSON.parse(raw) as IdentityConfig
  } catch {
    return null
  }
}

// Fetches the calling workspace's identity config. The API resolves the Tenant server-side from the
// subdomain (X-Forwarded-Host, per F7-3), so this MUST be called on the SPA's own origin (the tenant
// subdomain) — see sameOriginUsersApiUrl — not the fixed app-host base, which would resolve subdomain
// 'app' and 404. There's no query/body parameter. Returns null on 404 —
// the subdomain isn't a known workspace — which the loader renders as InvalidWorkspacePage (D23).
// Per Q8 the cache lives in sessionStorage (cleared on tab close); the endpoint's strong ETag lets a
// warm reload short-circuit to 304.
export async function fetchIdentityConfig(): Promise<IdentityConfig | null> {
  const cachedRaw = sessionStorage.getItem(STORAGE_KEY)
  const cachedEtag = sessionStorage.getItem(ETAG_KEY)

  const response = await axios.get<IdentityConfig>(`${sameOriginUsersApiUrl()}v1/auth/identity-config`, {
    // Only send If-None-Match when BOTH the validator AND a usable body are cached. Guarding on the etag
    // alone risks a 304 we can't satisfy: if the body write was lost (e.g. QuotaExceeded on the larger
    // body while the smaller etag persisted), the server would 304 and we'd fall through with no data,
    // crashing configureAuth() and trapping the user on the error page.
    headers: cachedEtag && cachedRaw ? { 'If-None-Match': cachedEtag } : undefined,
    validateStatus: (status) => status === 200 || status === 304 || status === 404,
  })

  if (response.status === 404) {
    sessionStorage.removeItem(STORAGE_KEY)
    sessionStorage.removeItem(ETAG_KEY)
    return null
  }

  if (response.status === 304 && cachedRaw) {
    return JSON.parse(cachedRaw) as IdentityConfig
  }

  // Reached only on 200: If-None-Match is sent only when a cached body also exists (above), so every
  // 304 is served by the branch above and never falls through here. Cache the body + validator for next time.
  const etag = response.headers.etag ?? response.headers.ETag
  if (typeof etag === 'string') {
    sessionStorage.setItem(ETAG_KEY, etag)
  }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(response.data))
  return response.data
}
